/**
 * GT 검증 Panel — Gemini Vision API 추출 결과 vs DB GT diff 검증
 *
 * 사용 흐름:
 *   1. 카테고리 선택 (L/RID/CONT/B/I)
 *   2. 좌측 diff list 에서 row 선택
 *   3. 우측 이미지 + 좌(DB GT) vs 우(Gemini) 비교, diff 필드만 highlight
 *   4. 액션:
 *      [채택 Gemini]  : Gemini 결과 전체 채택
 *      [DB 유지]       : 원본 DB GT 유지
 *      [필드별 채택]    : 필드별로 어느 쪽 또는 직접 입력
 *      [스킵]          : 다음 row
 *
 * 저장: backend/gt_verify_corrections.jsonl (POST /api/gt-verify/save)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw, CheckCircle, XCircle, Edit3, SkipForward, Loader, FileText, Image as ImageIcon, AlertTriangle } from 'lucide-react';

const API_BASE = '/api/gt-verify';

const STATUS_BADGE = {
    diff:  { label: 'DIFF',  cls: 'bg-amber-100 text-amber-800 border border-amber-300' },
    match: { label: 'MATCH', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-300' },
    fail:  { label: 'FAIL',  cls: 'bg-red-100 text-red-800 border border-red-300' },
};

const PAGE_SIZE = 30;

const GTVerifyPanel = () => {
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [statusFilter, setStatusFilter] = useState('diff');
    const [verifiedFilter, setVerifiedFilter] = useState('no');
    const [loadingList, setLoadingList] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [imgSrc, setImgSrc] = useState(null);
    const [manualFields, setManualFields] = useState({});
    const [savingAction, setSavingAction] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    }, []);

    const loadCategories = useCallback(async () => {
        try {
            const r = await fetch(`${API_BASE}/categories`);
            const j = await r.json();
            if (j.ok) {
                setCategories(j.categories || []);
                if (!activeCategory && j.categories?.length) {
                    setActiveCategory(j.categories[0].category);
                }
            }
        } catch (e) {
            showToast(`카테고리 로드 실패: ${e.message}`, 'error');
        }
    }, [activeCategory, showToast]);

    useEffect(() => { loadCategories(); }, [loadCategories]);

    const loadList = useCallback(async () => {
        if (!activeCategory) return;
        setLoadingList(true);
        try {
            const params = new URLSearchParams({
                category: activeCategory,
                offset: String(offset),
                limit: String(PAGE_SIZE),
            });
            if (statusFilter) params.set('status', statusFilter);
            if (verifiedFilter) params.set('verified', verifiedFilter);
            const r = await fetch(`${API_BASE}/diff?${params}`);
            const j = await r.json();
            if (j.ok) {
                setItems(j.items || []);
                setTotal(j.total || 0);
            }
        } catch (e) {
            showToast(`list 로드 실패: ${e.message}`, 'error');
        } finally {
            setLoadingList(false);
        }
    }, [activeCategory, offset, statusFilter, verifiedFilter, showToast]);

    useEffect(() => { loadList(); }, [loadList]);

    const loadDetail = useCallback(async (id) => {
        if (!id || !activeCategory) return;
        setLoadingDetail(true);
        setDetail(null);
        setImgSrc(null);
        setManualFields({});
        try {
            const params = new URLSearchParams({ category: activeCategory });
            const r = await fetch(`${API_BASE}/diff/${encodeURIComponent(id)}?${params}`);
            const j = await r.json();
            if (j.ok) {
                setDetail(j.item);
                if (j.item?.file_name) {
                    setImgSrc(`${API_BASE}/image?path=${encodeURIComponent(j.item.file_name)}`);
                }
                if (j.correction?.fields) {
                    setManualFields(j.correction.fields);
                }
            }
        } catch (e) {
            showToast(`상세 로드 실패: ${e.message}`, 'error');
        } finally {
            setLoadingDetail(false);
        }
    }, [activeCategory, showToast]);

    useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

    const saveAction = useCallback(async (action, fields = null, notes = '') => {
        if (!detail || !activeCategory) return;
        setSavingAction(true);
        try {
            const body = {
                id: detail.id,
                category: activeCategory,
                action,
                fields: fields || {},
                notes,
            };
            const r = await fetch(`${API_BASE}/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const j = await r.json();
            if (j.ok) {
                showToast(`저장됨: ${action}`, 'success');
                // 다음 row 자동 선택
                const idx = items.findIndex(x => x.id === detail.id);
                if (idx >= 0 && idx < items.length - 1) {
                    setSelectedId(items[idx + 1].id);
                } else if (offset + PAGE_SIZE < total) {
                    setOffset(offset + PAGE_SIZE);
                    setSelectedId(null);
                }
                loadList();
            } else {
                showToast(`저장 실패: ${j.error}`, 'error');
            }
        } catch (e) {
            showToast(`저장 실패: ${e.message}`, 'error');
        } finally {
            setSavingAction(false);
        }
    }, [detail, activeCategory, items, offset, total, loadList, showToast]);

    const sync = async () => {
        setSyncing(true);
        try {
            const r = await fetch(`${API_BASE}/sync`, { method: 'POST' });
            const j = await r.json();
            if (j.ok) {
                showToast(`동기화 완료: ${j.results.filter(x => x.ok).length}개 파일`, 'success');
                loadCategories();
                loadList();
            }
        } catch (e) {
            showToast(`동기화 실패: ${e.message}`, 'error');
        } finally {
            setSyncing(false);
        }
    };

    const pushAndApply = async (dryRun = false) => {
        if (!dryRun && !window.confirm('검증 결과를 116번 학습 데이터에 적용합니다. 계속?')) return;
        setSyncing(true);
        try {
            const r = await fetch(`${API_BASE}/push-and-apply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dry_run: dryRun }),
            });
            const j = await r.json();
            if (j.ok) {
                showToast(`${dryRun ? '[DRY-RUN] ' : ''}적용 완료. ${j.stdout?.split('\n').slice(-2).join(' ') || ''}`, 'success');
            } else {
                showToast(`적용 실패: ${j.error || j.stderr}`, 'error');
            }
        } catch (e) {
            showToast(`적용 실패: ${e.message}`, 'error');
        } finally {
            setSyncing(false);
        }
    };

    const allKeys = useMemo(() => {
        if (!detail) return [];
        const orig = detail.gt_original || {};
        const gem = detail.gt_gemini || {};
        return Array.from(new Set([...Object.keys(orig), ...Object.keys(gem)]));
    }, [detail]);

    const renderDiffField = (key) => {
        if (!detail) return null;
        const orig = detail.gt_original || {};
        const gem = detail.gt_gemini || {};
        const o = orig[key] !== undefined ? String(orig[key] ?? '') : '';
        const g = gem[key] !== undefined ? String(gem[key] ?? '') : '';
        const isDiff = o.trim() !== g.trim();
        const manualVal = manualFields[key] ?? null;

        return (
            <tr key={key} className={isDiff ? 'bg-amber-50' : ''}>
                <td className="px-2 py-1 text-xs font-mono text-gray-700 align-top w-32">{key}</td>
                <td className={`px-2 py-1 text-xs align-top ${isDiff ? 'text-red-700' : 'text-gray-600'}`}>{o || <span className="text-gray-300">∅</span>}</td>
                <td className={`px-2 py-1 text-xs align-top ${isDiff ? 'text-emerald-700 font-semibold' : 'text-gray-600'}`}>{g || <span className="text-gray-300">∅</span>}</td>
                <td className="px-2 py-1 align-top">
                    <input
                        type="text"
                        value={manualVal !== null ? manualVal : (isDiff ? g : o)}
                        onChange={e => setManualFields({ ...manualFields, [key]: e.target.value })}
                        className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded font-mono"
                    />
                </td>
                {isDiff && (
                    <td className="px-1 py-1 align-top">
                        <button
                            onClick={() => setManualFields({ ...manualFields, [key]: o })}
                            className="text-xs px-1 py-0.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="DB 값으로"
                        >←</button>
                        <button
                            onClick={() => setManualFields({ ...manualFields, [key]: g })}
                            className="text-xs px-1 py-0.5 text-emerald-600 hover:bg-emerald-50 rounded"
                            title="Gemini 값으로"
                        >→</button>
                    </td>
                )}
            </tr>
        );
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);
    const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

    return (
        <div className="flex flex-col h-full bg-gray-50">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-white border-b">
                <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <h2 className="text-lg font-semibold">GT 검증 (Gemini diff)</h2>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                        {categories.map(c => `${c.category}=${c.diff}/${c.total}`).join(' · ')}
                    </span>
                    <button
                        onClick={sync}
                        disabled={syncing}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
                        116 sync
                    </button>
                    <button
                        onClick={() => pushAndApply(true)}
                        disabled={syncing}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-amber-100 text-amber-700 rounded hover:bg-amber-200 disabled:opacity-50"
                        title="검증 결과를 116번에 dry-run 적용"
                    >
                        검증결과 dry-run
                    </button>
                    <button
                        onClick={() => pushAndApply(false)}
                        disabled={syncing}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                        title="검증 결과를 116번 학습 데이터에 최종 적용"
                    >
                        검증결과 최종 적용
                    </button>
                </div>
            </div>

            {/* Category tabs */}
            <div className="flex gap-1 px-4 py-2 bg-white border-b">
                {categories.map(c => (
                    <button
                        key={c.category}
                        onClick={() => { setActiveCategory(c.category); setOffset(0); setSelectedId(null); }}
                        className={`px-3 py-1.5 text-sm rounded ${activeCategory === c.category ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                    >
                        {c.category}
                        <span className="ml-1 text-xs opacity-70">{c.diff}/{c.total}</span>
                    </button>
                ))}
            </div>

            {/* filters */}
            <div className="flex gap-2 px-4 py-2 bg-white border-b text-xs">
                <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setOffset(0); }} className="px-2 py-1 border rounded">
                    <option value="">전체 상태</option>
                    <option value="diff">DIFF</option>
                    <option value="match">MATCH</option>
                    <option value="fail">FAIL</option>
                </select>
                <select value={verifiedFilter} onChange={e => { setVerifiedFilter(e.target.value); setOffset(0); }} className="px-2 py-1 border rounded">
                    <option value="">검증여부 무관</option>
                    <option value="no">미검증만</option>
                    <option value="yes">검증완료만</option>
                </select>
                <span className="text-gray-500 self-center">총 {total}건</span>
            </div>

            {/* main: list + detail */}
            <div className="flex flex-1 overflow-hidden">
                {/* left: list */}
                <div className="w-72 border-r bg-white overflow-y-auto">
                    {loadingList && (
                        <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
                            <Loader className="w-4 h-4 animate-spin mr-2" /> 로드 중...
                        </div>
                    )}
                    {items.map(it => (
                        <button
                            key={it.id}
                            onClick={() => setSelectedId(it.id)}
                            className={`w-full text-left px-3 py-2 border-b hover:bg-blue-50 ${selectedId === it.id ? 'bg-blue-100' : ''}`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-mono text-gray-600 truncate flex-1">{it.id}</span>
                                {it.verified && <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />}
                            </div>
                            <div className="flex items-center gap-1 mt-0.5">
                                <span className={`text-[10px] px-1 rounded ${STATUS_BADGE[it.status]?.cls || 'bg-gray-100'}`}>
                                    {STATUS_BADGE[it.status]?.label || it.status}
                                </span>
                                {it.n_diff_fields > 0 && (
                                    <span className="text-[10px] text-gray-500">{it.n_diff_fields} fields</span>
                                )}
                            </div>
                            {it.fields_changed?.length > 0 && (
                                <div className="text-[10px] text-gray-400 mt-0.5 truncate">
                                    {it.fields_changed.join(', ')}
                                </div>
                            )}
                        </button>
                    ))}
                    {!loadingList && items.length === 0 && (
                        <div className="text-center py-8 text-gray-400 text-sm">데이터 없음</div>
                    )}
                    {/* pagination */}
                    {totalPages > 1 && (
                        <div className="sticky bottom-0 bg-white border-t flex items-center justify-between px-2 py-1 text-xs">
                            <button
                                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                                disabled={offset === 0}
                                className="p-1 disabled:opacity-30"
                            ><ChevronLeft className="w-3 h-3" /></button>
                            <span>{currentPage} / {totalPages}</span>
                            <button
                                onClick={() => setOffset(Math.min(total - PAGE_SIZE, offset + PAGE_SIZE))}
                                disabled={offset + PAGE_SIZE >= total}
                                className="p-1 disabled:opacity-30"
                            ><ChevronRight className="w-3 h-3" /></button>
                        </div>
                    )}
                </div>

                {/* right: detail */}
                <div className="flex-1 flex overflow-hidden">
                    {!detail ? (
                        <div className="flex-1 flex items-center justify-center text-gray-400">
                            {loadingDetail ? <Loader className="w-6 h-6 animate-spin" /> : '좌측에서 row를 선택하세요'}
                        </div>
                    ) : (
                        <>
                            {/* image */}
                            <div className="w-1/2 bg-gray-100 overflow-auto p-3">
                                {imgSrc ? (
                                    <img src={imgSrc} alt={detail.id} className="max-w-full" onError={() => setImgSrc(null)} />
                                ) : (
                                    <div className="text-gray-400 flex items-center justify-center h-32">
                                        <ImageIcon className="w-6 h-6 mr-2" /> 이미지 없음
                                    </div>
                                )}
                            </div>
                            {/* diff table + actions */}
                            <div className="w-1/2 overflow-auto bg-white">
                                <div className="px-3 py-2 border-b bg-gray-50">
                                    <div className="text-xs text-gray-500">{detail.id}</div>
                                    <div className="text-xs font-mono text-gray-400 mt-0.5 truncate">{detail.file_name}</div>
                                </div>
                                <table className="w-full text-xs">
                                    <thead className="sticky top-0 bg-gray-100">
                                        <tr>
                                            <th className="px-2 py-1 text-left">field</th>
                                            <th className="px-2 py-1 text-left text-red-600">DB GT</th>
                                            <th className="px-2 py-1 text-left text-emerald-600">Gemini</th>
                                            <th className="px-2 py-1 text-left">최종</th>
                                            <th className="w-16"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allKeys.map(renderDiffField)}
                                    </tbody>
                                </table>
                                {/* actions */}
                                <div className="sticky bottom-0 bg-white border-t p-2 flex flex-wrap gap-1">
                                    <button
                                        onClick={() => saveAction('accept_gemini', detail.gt_gemini || {})}
                                        disabled={savingAction}
                                        className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
                                    >
                                        <CheckCircle className="w-3 h-3" /> Gemini 전체 채택
                                    </button>
                                    <button
                                        onClick={() => saveAction('keep_db', detail.gt_original || {})}
                                        disabled={savingAction}
                                        className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 flex items-center gap-1"
                                    >
                                        <XCircle className="w-3 h-3" /> DB 유지
                                    </button>
                                    <button
                                        onClick={() => saveAction('manual', manualFields)}
                                        disabled={savingAction}
                                        className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                                    >
                                        <Edit3 className="w-3 h-3" /> 직접 입력 저장
                                    </button>
                                    <button
                                        onClick={() => saveAction('skip')}
                                        disabled={savingAction}
                                        className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded hover:bg-amber-200 disabled:opacity-50 flex items-center gap-1"
                                    >
                                        <SkipForward className="w-3 h-3" /> 스킵
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* toast */}
            {toast && (
                <div className={`fixed bottom-4 right-4 px-3 py-2 rounded shadow text-xs ${
                    toast.type === 'error' ? 'bg-red-600 text-white' :
                    toast.type === 'success' ? 'bg-emerald-600 text-white' :
                    'bg-gray-700 text-white'
                }`}>
                    {toast.msg}
                </div>
            )}
        </div>
    );
};

export default GTVerifyPanel;
